
import React, { useCallback, useMemo } from "react";
import { Movie } from "@/types/movie";
import { useMovieContext } from "@/context";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import DialogPoster from "./dialog/DialogPoster";
import DialogDetails from "./dialog/DialogDetails";
import MovieErrorRetry from "@/components/ui/MovieErrorRetry";
import { Loader2 } from "lucide-react";

interface MovieDetailsDialogProps {
  movie: Movie | null;
  isExpanded: boolean;
  setIsExpanded: (value: boolean) => void;
  fromCard?: boolean;
  currentTab?: string;
  viewVariant?: "grid" | "list" | "search" | "detail";
  isLoading?: boolean;
  loadError?: string | null;
  onRetry?: () => void;
}

const MovieDetailsDialog: React.FC<MovieDetailsDialogProps> = ({ 
  movie, 
  isExpanded, 
  setIsExpanded,
  fromCard = false,
  currentTab,
  viewVariant = "detail",
  isLoading = false,
  loadError = null,
  onRetry
}) => {
  const { allMovies, commitToggleFavorite, commitTogglePlanToWatch } = useMovieContext();
  const isMobile = useIsMobile();
  
  // FIXED: Always get the latest movie from allMovies first, fallback to movie prop
  const displayMovie = useMemo(() => {
    if (!movie?.tmdbId) return movie;
    return allMovies.find(m => m.tmdbId === movie.tmdbId) || movie;
  }, [allMovies, movie]);
  
  // ENHANCED: Use commit handlers that ensure complete TMDB metadata
  const handleToggleFavoriteUnified = useCallback(async () => {
    if (!displayMovie?.tmdbId) return;
    
    try {
      // Use the enhanced commit handler with TMDB metadata fetching
      await commitToggleFavorite(displayMovie);
    } catch (error) {
      console.error("Error toggling favorite in dialog:", error);
    }
  }, [displayMovie, commitToggleFavorite]);
  
  const handleTogglePlanToWatchUnified = useCallback(async () => {
    if (!displayMovie?.tmdbId) return;
    
    try {
      // Use the enhanced commit handler with TMDB metadata fetching
      await commitTogglePlanToWatch(displayMovie);
    } catch (error) {
      console.error("Error toggling watchlist in dialog:", error);
    }
  }, [displayMovie, commitTogglePlanToWatch]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsExpanded(open);
  }, [setIsExpanded]);

  // If there's no movie and we're not loading, don't render anything
  if (!movie && !isLoading && !loadError) return null;

  return (
    <Dialog open={isExpanded} onOpenChange={handleOpenChange}>
      <DialogContent 
        className={`
          p-0 bg-transparent overflow-hidden
          ${isMobile 
            ? "max-w-[95vw] max-h-[95vh] w-full h-full" 
            : "max-w-5xl"
          }
        `} 
        data-dialog-id={displayMovie?.tmdbId}
      >
        <DialogTitle className="sr-only">{displayMovie?.title || 'Movie'} Details</DialogTitle>
        <DialogDescription className="sr-only">
          {displayMovie?.year} film directed by {displayMovie?.director}
        </DialogDescription>
        
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-64 bg-mono-platinum p-6 md:p-8 rounded-lg">
            <Loader2 className="w-10 h-10 text-mono-onyx/70 animate-spin mb-4" />
            <p className="mt-4 text-mono-onyx/70">Loading movie details...</p>
          </div>
        ) : loadError ? (
          <div className="bg-mono-platinum rounded-lg">
            <MovieErrorRetry
              error={loadError}
              onRetry={onRetry || (() => {})}
              className="h-64"
            />
          </div>
        ) : movie ? (
          <div className={`
            flex h-full rounded-lg overflow-hidden
            ${isMobile ? "flex-col" : "flex-col md:flex-row"}
          `}>
            <DialogPoster movie={displayMovie} variant="dialog" />
            <DialogDetails
              movie={displayMovie}
              onToggleFavorite={handleToggleFavoriteUnified}
              onTogglePlanToWatch={handleTogglePlanToWatchUnified}
              variant="dialog"
            />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(MovieDetailsDialog);
